/**
 * The render agent: owns the Three.js scene, the board, part views, the drag ghost,
 * selection, glow lights, electricity flow, day/night lighting, and the camera.
 * Reads the board + simulation; never mutates them.
 */
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { footprint, type Board, type FootCell } from '../core/board';
import type { Placement } from '../core/types';
import { partDef } from '../parts/registry';
import type { Sim } from '../sim/sim';
import { Flow, type FlowSource } from './flow';
import { COLORS, LAYER_H, PLATE_H, cylinder, slabGeometry } from './kit';
import { createView, plateSize, type PartView } from './views';

export type Quality = 'low' | 'medium' | 'high';

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface Entry {
  view: PartView;
  /** Text decals kept upright on screen. */
  upright: THREE.Object3D[];
  sig: string;
  pos: THREE.Vector3;
  rotY: number;
  /** Seconds since the part was dropped (for the snap squash). */
  dropT: number;
}

const LIGHT_POOL = 4;
/** How close (studs) to a snap's center a touch must be to grab the snap. */
const SNAP_REACH = 0.42;

/**
 * Grid cell → world position (center of the stud), at a given layer.
 * World space is anchored to the build (board.ox/oy), so growing the board never moves parts.
 */
export function cellToWorld(board: Board, x: number, y: number, layer = 0, out = new THREE.Vector3()) {
  return out.set(x - board.ox, layer * LAYER_H, y - board.oy);
}

export function placementTransform(board: Board, p: Placement): { pos: THREE.Vector3; rotY: number } {
  const cells = footprint(p);
  const a = cells[0]!;
  const b = cells[cells.length - 1]!;
  const pos = cellToWorld(board, (a.x + b.x) / 2, (a.y + b.y) / 2, p.layer);
  return { pos, rotY: (-p.rot * Math.PI) / 2 };
}

export interface Pick {
  id: string;
  /** The snap that was touched, if any. */
  pin: number | null;
}

export class Stage {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(30, 1, 0.1, 400);
  readonly root = new THREE.Group();
  readonly flow = new Flow();
  private boardGroup = new THREE.Group();
  private entries = new Map<string, Entry>();
  private hemi: THREE.HemisphereLight;
  private key: THREE.DirectionalLight;
  private pool: THREE.PointLight[] = [];
  private raycaster = new THREE.Raycaster();
  private ghost?: { view: PartView; type: string; sig: string };
  private ghostCells = new THREE.Group();
  private selection: THREE.Mesh;
  private selectionMat: THREE.MeshBasicMaterial;
  selectedId: string | null = null;
  insets: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
  /** Camera azimuth: 0 = landscape, π/2 = rotated for portrait screens. */
  private azimuth = 0;
  private time = 0;
  private boardSig = '';
  shake = 0;

  // Camera: a "fit" goal for the board, plus the kid's own zoom & pan on top. We glide to it.
  private fitTarget = new THREE.Vector3();
  private fitDist = 20;
  private camTarget = new THREE.Vector3();
  private camDist = 20;
  private camDir = new THREE.Vector3(0, 1, 1).normalize();
  userZoom = 1;
  userPan = new THREE.Vector3();

  /** 0 = day, 1 = lights out. */
  night = false;
  private nightK = 0;

  constructor(
    readonly canvas: HTMLCanvasElement,
    private board: Board,
    readonly quality: Quality,
  ) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: quality !== 'low',
      alpha: true,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio || 1, quality === 'high' ? 2 : quality === 'medium' ? 1.5 : 1),
    );
    this.renderer.toneMapping = THREE.NeutralToneMapping;
    this.renderer.toneMappingExposure = 0.95;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = quality !== 'low';
    this.renderer.shadowMap.type = THREE.PCFShadowMap;

    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 0.55;
    pmrem.dispose();

    this.hemi = new THREE.HemisphereLight('#ffffff', '#b9a8ff', 0.45);
    this.scene.add(this.hemi);
    this.key = new THREE.DirectionalLight('#ffffff', 1.9);
    this.key.castShadow = quality !== 'low';
    this.key.shadow.mapSize.setScalar(quality === 'high' ? 2048 : 1024);
    this.key.shadow.bias = -0.0004;
    this.key.shadow.normalBias = 0.02;
    this.scene.add(this.key, this.key.target);

    for (let i = 0; i < LIGHT_POOL; i++) {
      const l = new THREE.PointLight('#ffbe5c', 0, 6, 1.6);
      this.pool.push(l);
      this.scene.add(l);
    }

    this.scene.add(this.root);
    this.root.add(this.boardGroup, this.ghostCells, this.flow.points);

    this.selectionMat = new THREE.MeshBasicMaterial({
      color: '#7c5cff',
      transparent: true,
      opacity: 0.0,
      depthWrite: false,
    });
    this.selection = new THREE.Mesh(new THREE.BufferGeometry(), this.selectionMat);
    this.selection.renderOrder = 2;
    this.root.add(this.selection);

    this.buildBoard();
  }

  setBoard(board: Board) {
    this.board = board;
    for (const id of [...this.entries.keys()]) this.removeEntry(id);
    this.boardSig = '';
    this.buildBoard();
    this.resetView(true);
  }

  // ───────────────────────────── Board ─────────────────────────────
  private boardMats = {
    frame: new THREE.MeshStandardMaterial({ color: COLORS.boardRim, roughness: 0.35 }),
    top: new THREE.MeshStandardMaterial({ color: COLORS.board, roughness: 0.55 }),
    stud: new THREE.MeshStandardMaterial({ color: COLORS.stud, roughness: 0.5 }),
  };

  /** (Re)build the board mesh when its size or position changes. */
  private buildBoard() {
    const { cols, rows, ox, oy } = this.board;
    const sig = `${cols}x${rows}@${ox},${oy}`;
    if (sig === this.boardSig) return;
    const grew = this.boardSig !== '';
    this.boardSig = sig;
    for (const c of this.boardGroup.children) if (c instanceof THREE.InstancedMesh) c.dispose();
    this.boardGroup.clear();
    const frame = new THREE.Mesh(slabGeometry(cols + 1.0, rows + 1.0, 0.34, 0.55), this.boardMats.frame);
    frame.position.y = -0.42;
    frame.receiveShadow = true;
    frame.castShadow = true;
    const top = new THREE.Mesh(slabGeometry(cols + 0.5, rows + 0.5, 0.12, 0.35), this.boardMats.top);
    top.position.y = -0.12;
    top.receiveShadow = true;

    const studs = new THREE.InstancedMesh(cylinder(0.17, 0.19, 0.05, 20), this.boardMats.stud, cols * rows);
    const m = new THREE.Matrix4();
    let i = 0;
    for (let y = 0; y < rows; y++)
      for (let x = 0; x < cols; x++) {
        m.makeTranslation(x - (cols - 1) / 2, 0.012, y - (rows - 1) / 2);
        studs.setMatrixAt(i++, m);
      }
    studs.receiveShadow = true;

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(400, 400).rotateX(-Math.PI / 2),
      new THREE.ShadowMaterial({ opacity: 0.16 }),
    );
    ground.position.y = -0.43;
    ground.receiveShadow = true;
    this.boardGroup.add(frame, top, studs, ground);
    this.boardGroup.position.copy(this.boardCenter());
    if (grew) {
      // A little "boing" so kids notice the board grew for them.
      this.boardGroup.scale.set(0.97, 1, 0.97);
    }

    const ext = Math.max(cols, rows) / 2 + 3;
    const cam = this.key.shadow.camera;
    cam.left = -ext;
    cam.right = ext;
    cam.top = ext;
    cam.bottom = -ext;
    cam.near = 1;
    cam.far = 60;
    cam.updateProjectionMatrix();
    this.fit();
  }

  private boardCenter(out = new THREE.Vector3()) {
    return out.set((this.board.cols - 1) / 2 - this.board.ox, 0, (this.board.rows - 1) / 2 - this.board.oy);
  }

  // ───────────────────────────── Parts ─────────────────────────────
  /** Bring views in line with the board. New parts drop in with a snap (unless `instant`). */
  sync(instant = false) {
    this.buildBoard();
    const seen = new Set<string>();
    for (const p of this.board.parts.values()) {
      seen.add(p.id);
      const { on: _on, ...shapeProps } = p.props;
      const sig = `${p.type}|${JSON.stringify(shapeProps)}`;
      const t = placementTransform(this.board, p);
      let e = this.entries.get(p.id);
      if (e && e.sig !== sig) {
        this.removeEntry(p.id);
        e = undefined;
      }
      if (!e) {
        const view = createView(p);
        const upright: THREE.Object3D[] = [];
        view.group.traverse((o) => o.userData.upright && upright.push(o));
        e = { view, sig, pos: t.pos.clone(), rotY: t.rotY, dropT: instant ? 99 : 0, upright };
        view.group.position.copy(t.pos);
        view.group.rotation.y = t.rotY;
        this.root.add(view.group);
        this.entries.set(p.id, e);
      } else {
        e.pos.copy(t.pos);
        e.rotY = t.rotY;
      }
    }
    for (const id of [...this.entries.keys()]) if (!seen.has(id)) this.removeEntry(id);
  }

  private removeEntry(id: string) {
    const e = this.entries.get(id);
    if (!e) return;
    this.root.remove(e.view.group);
    e.view.dispose();
    this.entries.delete(id);
  }

  /** Mark a part as just dropped so it plays the snap squash. */
  bump(id: string) {
    const e = this.entries.get(id);
    if (e) e.dropT = 0;
  }

  viewOf(id: string): PartView | undefined {
    return this.entries.get(id)?.view;
  }

  /** Hide a part while it's being dragged around (the ghost stands in for it). */
  setHidden(id: string | null) {
    for (const [pid, e] of this.entries) e.view.group.visible = pid !== id;
  }

  // ───────────────────────────── Ghost (drag preview) ─────────────────────────────
  /**
   * Show a preview of a part. `float` = how far above its landing spot it hovers
   * (dragging parts hover; wires being drawn sit exactly where they'll go).
   */
  showGhost(
    p: Placement,
    world: THREE.Vector3 | null,
    cells: FootCell[] | null,
    valid: boolean,
    layer: number,
    float = 0.55,
  ) {
    const sig = `${p.type}|${JSON.stringify(p.props)}|${p.rot}`;
    if (!this.ghost || this.ghost.sig !== sig) {
      this.hideGhost(false);
      const view = createView({ ...p, id: '__ghost' });
      view.group.traverse((o) => (o.userData.partId = undefined));
      this.ghost = { view, type: p.type, sig };
      this.root.add(view.group);
      if (!world) {
        const t = placementTransform(this.board, { ...p, layer });
        view.group.position.copy(t.pos);
      }
    }
    const g = this.ghost.view.group;
    g.visible = true;
    const t = placementTransform(this.board, { ...p, layer });
    const target = world ? world.clone() : t.pos.clone();
    target.y = Math.max(target.y, layer * LAYER_H) + float;
    const fresh = g.position.lengthSq() === 0;
    g.position.lerp(target, fresh || !world ? 1 : 0.5);
    g.rotation.y = world ? damp(g.rotation.y, t.rotY, 0.5) : t.rotY;
    g.scale.setScalar(world ? 1.06 : 1);
    this.ghost.view.update(p, {
      board: this.board,
      state: partDef(p.type).initState(),
      reading: { current: 0, voltage: 0 },
      input: {},
      time: this.time,
      dt: 0,
    });

    this.clearGhostCells();
    if (cells) {
      for (const c of cells) {
        const m = new THREE.Mesh(
          slabGeometry(0.86, 0.86, 0.02, 0.2),
          new THREE.MeshBasicMaterial({
            color: valid ? (c.pin >= 0 ? '#2bd97c' : '#8ef0b8') : '#ff4d6d',
            transparent: true,
            opacity: 0.75,
            depthWrite: false,
          }),
        );
        cellToWorld(this.board, c.x, c.y, layer, m.position);
        m.position.y += 0.005;
        m.renderOrder = 3;
        this.ghostCells.add(m);
      }
    }
  }

  hideGhost(cells = true) {
    if (this.ghost) {
      this.root.remove(this.ghost.view.group);
      this.ghost.view.dispose();
      this.ghost = undefined;
    }
    if (cells) this.clearGhostCells();
  }

  clearGhostCells() {
    for (const c of this.ghostCells.children) ((c as THREE.Mesh).material as THREE.Material).dispose();
    this.ghostCells.clear();
  }

  // ───────────────────────────── Snap targets (magnetic hints while drawing) ─────────────────────────────
  private targets = new THREE.Group();
  private targetMat = new THREE.MeshBasicMaterial({
    color: '#ffe14d',
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
  });

  /** Glowing rings on every snap you could connect a wire to. */
  showSnapTargets(cells: { x: number; y: number; layer: number }[]) {
    this.hideSnapTargets();
    for (const c of cells) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.24, 0.34, 32).rotateX(-Math.PI / 2),
        this.targetMat,
      );
      cellToWorld(this.board, c.x, c.y, c.layer, ring.position);
      ring.position.y += 0.26;
      ring.renderOrder = 4;
      this.targets.add(ring);
    }
    this.root.add(this.targets);
  }

  hideSnapTargets() {
    for (const c of this.targets.children) (c as THREE.Mesh).geometry.dispose();
    this.targets.clear();
    this.root.remove(this.targets);
  }

  // ───────────────────────────── Hint ghost ─────────────────────────────
  private hint?: { view: PartView; mats: THREE.Material[]; sig: string; baseY: number };

  /** A see-through, shimmering outline of where the next part goes. */
  showHint(p: Placement | null) {
    const sig = p ? `${p.type}|${p.x},${p.y},${p.rot}|${JSON.stringify(p.props)}` : '';
    if (this.hint?.sig === sig) return;
    if (this.hint) {
      this.root.remove(this.hint.view.group);
      this.hint.mats.forEach((m) => m.dispose());
      this.hint.view.dispose();
      this.hint = undefined;
    }
    if (!p) return;
    const view = createView({ ...p, id: '__hint' });
    const mats: THREE.Material[] = [];
    view.group.traverse((o) => {
      o.userData.partId = undefined;
      o.userData.pin = undefined;
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) {
        const m = new THREE.MeshBasicMaterial({
          color: '#8fe9ff',
          transparent: true,
          opacity: 0.35,
          depthWrite: false,
        });
        mesh.material = m;
        mesh.castShadow = false;
        mats.push(m);
      } else if ((o as THREE.Sprite).isSprite) o.visible = false;
    });
    const t = placementTransform(this.board, { ...p, layer: this.board.check(p).layer });
    view.group.position.copy(t.pos);
    view.group.position.y += 0.02;
    view.group.rotation.y = t.rotY;
    this.root.add(view.group);
    this.hint = { view, mats, sig, baseY: view.group.position.y };
  }

  // ───────────────────────────── Selection ─────────────────────────────
  select(id: string | null) {
    this.selectedId = id;
    const p = id ? this.board.parts.get(id) : undefined;
    if (!p) {
      this.selectedId = null;
      return;
    }
    const { len, w } = plateSize(p);
    this.selection.geometry = slabGeometry(len + 0.22, w + 0.22, 0.03, 0.4);
  }

  // ───────────────────────────── Frame update ─────────────────────────────
  update(sim: Sim, dt: number) {
    this.time += dt;
    const flowSources: FlowSource[] = [];
    for (const [id, e] of this.entries) {
      const p = this.board.parts.get(id);
      if (!p) continue;
      const g = e.view.group;
      // Glide to position, then squash-and-stretch when snapped in.
      g.position.x = damp(g.position.x, e.pos.x, 0.45);
      g.position.z = damp(g.position.z, e.pos.z, 0.45);
      e.dropT += dt;
      const d = e.dropT;
      let lift = 0;
      let sy = 1;
      if (d < 0.35) {
        const u = d / 0.35;
        lift = Math.max(0, (1 - u) ** 3 * 0.6);
        sy = 1 - Math.sin(Math.min(1, u * 1.4) * Math.PI) * 0.18;
      }
      g.position.y = e.pos.y + lift;
      g.scale.set(1 + (1 - sy) * 0.5, sy, 1 + (1 - sy) * 0.5);
      g.rotation.y = e.rotY;
      // Keep words readable: undo the part's turn, then face the camera's "up".
      for (const u of e.upright) u.rotation.y = this.azimuth - e.rotY;
      const reading = sim.reading(id);
      e.view.update(p, {
        board: this.board,
        state: sim.states.get(id) ?? partDef(p.type).initState(),
        reading,
        input: sim.inputs.get(id) ?? {},
        time: this.time,
        dt,
      });
      if (e.view.flow && g.visible) {
        g.updateMatrixWorld();
        flowSources.push({ id, curve: e.view.flow, matrix: g.matrixWorld, current: reading.current });
      }
    }
    this.flow.boost = this.nightK;
    this.flow.update(flowSources, dt);

    // Selection outline follows its part.
    const sel = this.selectedId ? this.entries.get(this.selectedId) : undefined;
    if (sel && sel.view.group.visible) {
      this.selection.visible = true;
      this.selection.position.copy(sel.view.group.position);
      this.selection.position.y -= 0.012;
      this.selection.rotation.y = sel.rotY;
      this.selectionMat.opacity = 0.55 + Math.sin(this.time * 5) * 0.2;
    } else {
      this.selection.visible = false;
    }

    // Day ↔ night.
    this.nightK = damp(this.nightK, this.night ? 1 : 0, 1 - Math.exp(-4 * dt));
    const k = this.nightK;
    this.hemi.intensity = 0.45 * (1 - k) + 0.025 * k;
    this.key.intensity = 1.9 * (1 - k) + 0.07 * k;
    this.scene.environmentIntensity = 0.55 * (1 - k) + 0.04 * k;
    this.updateLights(1 + k * 1.8);

    for (const t of this.targets.children) t.scale.setScalar(1 + 0.15 * Math.sin(this.time * 8));
    this.targetMat.opacity = 0.45 + 0.3 * (0.5 + 0.5 * Math.sin(this.time * 8));

    if (this.hint) {
      const hk = 0.22 + 0.2 * (0.5 + 0.5 * Math.sin(this.time * 4));
      for (const m of this.hint.mats) (m as THREE.MeshBasicMaterial).opacity = hk;
      this.hint.view.group.position.y = this.hint.baseY + 0.05 + Math.sin(this.time * 3) * 0.04;
    }

    // The board "boing" when it grows.
    const bs = this.boardGroup.scale;
    bs.x = bs.z = damp(bs.x, 1, 1 - Math.exp(-10 * dt));

    // Camera glide.
    const goalTarget = this.fitTarget.clone().add(this.userPan);
    const goalDist = this.fitDist / this.userZoom;
    const a = 1 - Math.exp(-7 * dt);
    this.camTarget.lerp(goalTarget, a);
    this.camDist += (goalDist - this.camDist) * a;
    this.place(this.camDir, this.camTarget, this.camDist);
    this.key.position.copy(this.camTarget).add(new THREE.Vector3(-6, 14, 8));
    this.key.target.position.copy(this.camTarget);

    this.shake = Math.max(0, this.shake - dt * 2.5);
  }

  /** Give the brightest bulbs/LEDs a real light, so they light up the board around them. */
  private updateLights(boost: number) {
    const emitters: { pos: THREE.Vector3; color: THREE.Color; intensity: number }[] = [];
    for (const e of this.entries.values())
      for (const em of e.view.emitters)
        if (em.intensity > 0.05)
          emitters.push({
            pos: em.at.getWorldPosition(new THREE.Vector3()),
            color: em.color,
            intensity: em.intensity,
          });
    emitters.sort((a, b) => b.intensity - a.intensity);
    this.pool.forEach((l, i) => {
      const em = emitters[i];
      if (!em) {
        l.intensity = 0;
        return;
      }
      l.position.copy(em.pos).add(new THREE.Vector3(0, 0.35, 0));
      l.color.copy(em.color);
      l.intensity = em.intensity * 3.2 * boost;
      l.distance = 5 + boost * 2;
    });
  }

  render() {
    const saved = this.camera.position.clone();
    if (this.shake > 0) {
      const s = this.shake * 0.06;
      this.camera.position.add(new THREE.Vector3((Math.random() - 0.5) * s, 0, (Math.random() - 0.5) * s));
      this.camera.updateMatrixWorld();
    }
    this.renderer.render(this.scene, this.camera);
    this.camera.position.copy(saved);
  }

  // ───────────────────────────── Camera ─────────────────────────────
  resize() {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.fit(true);
  }

  /** Back to "whole board in view". */
  resetView(instant = false) {
    this.userZoom = 1;
    this.userPan.set(0, 0, 0);
    this.fit(instant);
  }

  zoomBy(f: number) {
    this.userZoom = Math.min(3.5, Math.max(0.6, this.userZoom * f));
  }

  /** Drag the world by a screen-space delta (px). */
  panBy(dxPx: number, dyPx: number) {
    const r = this.canvas.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const a = this.planePoint(cx, cy);
    const b = this.planePoint(cx + dxPx, cy + dyPx);
    if (!a || !b) return;
    this.userPan.sub(b.sub(a));
    // Don't let the board wander off screen.
    const lim = Math.max(this.board.cols, this.board.rows) / 2 + 2;
    this.userPan.x = Math.max(-lim, Math.min(lim, this.userPan.x));
    this.userPan.z = Math.max(-lim, Math.min(lim, this.userPan.z));
    this.userPan.y = 0;
  }

  /** Compute where the camera should be to frame the whole board in the safe area. */
  /**
   * Frame the whole board in the safe area. The camera tries both ways round (board wide,
   * or turned a quarter so it stands tall) and keeps whichever makes the board BIGGER, so
   * phones in portrait get a tall board that fills the screen.
   */
  fit(instant = false) {
    const W = this.canvas.clientWidth;
    const H = this.canvas.clientHeight;
    if (!W || !H) return;
    const { top, right, bottom, left } = this.insets;
    const sw = Math.max(50, W - left - right);
    const sh = Math.max(50, H - top - bottom);
    this.camera.aspect = W / H;
    // Shift the principal point to the center of the safe area.
    const dx = left + sw / 2 - W / 2;
    const dy = top + sh / 2 - H / 2;
    this.camera.setViewOffset(W, H, -dx, -dy, W, H);

    const c = this.boardCenter();
    const target = new THREE.Vector3(c.x, -0.2, c.z);
    const options = [0, Math.PI / 2].map((az) => ({
      az,
      dist: this.fitDistance(az, target, W, H, sw, sh, dx, dy),
    }));
    // Prefer the current orientation unless the other is clearly better (no flip-flopping on resize).
    const cur = options.find((o) => o.az === this.azimuth) ?? options[0]!;
    const best = options.reduce((a, b) => (b.dist < a.dist ? b : a));
    const pick = best.dist < cur.dist * 0.96 ? best : cur;
    this.azimuth = pick.az;
    this.setCamDir(pick.az);
    this.fitTarget.copy(target);
    this.fitDist = pick.dist;
    if (instant) {
      this.camTarget.copy(target).add(this.userPan);
      this.camDist = pick.dist / this.userZoom;
    }
    this.place(this.camDir, this.camTarget, this.camDist);
  }

  private setCamDir(az: number) {
    const elev = THREE.MathUtils.degToRad(57);
    this.camDir.set(Math.sin(az) * Math.cos(elev), Math.sin(elev), Math.cos(az) * Math.cos(elev));
  }

  /** Closest camera distance at which the board still fits the safe area, for one orientation. */
  private fitDistance(
    az: number,
    target: THREE.Vector3,
    W: number,
    H: number,
    sw: number,
    sh: number,
    dx: number,
    dy: number,
  ): number {
    this.setCamDir(az);
    const hx = (this.board.cols + 1) / 2;
    const hz = (this.board.rows + 1) / 2;
    const corners: THREE.Vector3[] = [];
    for (const x of [-hx, hx])
      for (const z of [-hz, hz])
        for (const y of [0, 0.9]) corners.push(new THREE.Vector3(target.x + x, y, target.z + z));
    let lo = 2;
    let hi = 300;
    const fx = (sw / W) * 0.975;
    const fy = (sh / H) * 0.975;
    for (let i = 0; i < 30; i++) {
      const mid = (lo + hi) / 2;
      this.place(this.camDir, target, mid);
      let fits = true;
      for (const v0 of corners) {
        const v = v0.clone().project(this.camera);
        const nx = v.x - (2 * dx) / W;
        const ny = v.y + (2 * dy) / H;
        if (Math.abs(nx) > fx || Math.abs(ny) > fy) {
          fits = false;
          break;
        }
      }
      if (fits) hi = mid;
      else lo = mid;
    }
    return hi;
  }

  private place(dir: THREE.Vector3, target: THREE.Vector3, dist: number) {
    this.camera.position.copy(target).addScaledVector(dir, dist);
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(target);
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld();
  }

  // ───────────────────────────── Picking ─────────────────────────────
  private ndc(clientX: number, clientY: number): THREE.Vector2 {
    const r = this.canvas.getBoundingClientRect();
    return new THREE.Vector2(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
  }

  /** Topmost part under the pointer, and which snap (if a snap was touched). */
  pick(clientX: number, clientY: number): Pick | null {
    this.raycaster.setFromCamera(this.ndc(clientX, clientY), this.camera);
    const objs = [...this.entries.values()].filter((e) => e.view.group.visible).map((e) => e.view.group);
    const hits = this.raycaster.intersectObjects(objs, true);
    for (const h of hits) {
      const id = h.object.userData.partId as string | undefined;
      if (!id) continue;
      const pin = h.object.userData.pin as number | undefined;
      if (pin !== undefined) return { id, pin };
      // Fat-finger friendly: touching the plastic right next to a snap counts as the snap.
      // Measure where the pointer ray crosses each snap's own height (tall parts can hide snaps).
      const p = this.board.parts.get(id);
      if (p) {
        let best = SNAP_REACH;
        let near: number | null = null;
        const ray = this.raycaster.ray;
        const at = new THREE.Vector3();
        for (const c of footprint(p)) {
          if (c.pin < 0) continue;
          const w = cellToWorld(this.board, c.x, c.y, p.layer);
          const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -(w.y + PLATE_H + 0.08));
          if (!ray.intersectPlane(plane, at)) continue;
          const d = Math.hypot(w.x - at.x, w.z - at.z);
          if (d < best) {
            best = d;
            near = c.pin;
          }
        }
        return { id, pin: near };
      }
      return { id, pin: null };
    }
    return null;
  }

  private planePoint(clientX: number, clientY: number, y = 0): THREE.Vector3 | null {
    this.raycaster.setFromCamera(this.ndc(clientX, clientY), this.camera);
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -y);
    const hit = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(plane, hit) ? hit : null;
  }

  /** Point on a horizontal plane at height `y`, in grid coordinates (fractional). */
  gridPoint(
    clientX: number,
    clientY: number,
    y = 0,
  ): { gx: number; gy: number; world: THREE.Vector3 } | null {
    const hit = this.planePoint(clientX, clientY, y);
    if (!hit) return null;
    return { gx: hit.x + this.board.ox, gy: hit.z + this.board.oy, world: hit };
  }

  /** Screen position (CSS px) of a part, for floating menus and speech bubbles. */
  screenOf(id: string, lift = 0.6): { x: number; y: number } | null {
    const e = this.entries.get(id);
    if (!e) return null;
    const v = e.view.group.position.clone();
    v.y += lift;
    return this.toScreen(v);
  }

  /** Screen position of a grid stud. */
  screenOfCell(x: number, y: number, layer = 0): { x: number; y: number } {
    return this.toScreen(cellToWorld(this.board, x, y, layer));
  }

  toScreen(v: THREE.Vector3): { x: number; y: number } {
    const p = v.clone().project(this.camera);
    const r = this.canvas.getBoundingClientRect();
    return { x: r.left + ((p.x + 1) / 2) * r.width, y: r.top + ((1 - p.y) / 2) * r.height };
  }

  /** Where the board (with its frame) currently sits on screen, in CSS px. For layout checks. */
  boardScreenRect(): { left: number; top: number; right: number; bottom: number } {
    const c = this.boardCenter();
    const hx = (this.board.cols + 1) / 2;
    const hz = (this.board.rows + 1) / 2;
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;
    for (const x of [-hx, hx])
      for (const z of [-hz, hz])
        for (const y of [-0.4, 0.2]) {
          const p = this.toScreen(new THREE.Vector3(c.x + x, y, c.z + z));
          left = Math.min(left, p.x);
          right = Math.max(right, p.x);
          top = Math.min(top, p.y);
          bottom = Math.max(bottom, p.y);
        }
    return { left, top, right, bottom };
  }

  get portrait() {
    return this.azimuth !== 0;
  }

  dispose() {
    for (const id of [...this.entries.keys()]) this.removeEntry(id);
    this.renderer.dispose();
  }
}

function damp(cur: number, target: number, k: number) {
  return cur + (target - cur) * k;
}

export { PLATE_H };
